<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Offers', to: '/settings/vehicles/offers' }
    ]"
  >
    <template #right>
      <Button
        icon="plus"
        label="New Offer"
        severity="primary"
        @click="openCreateModal()"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      v-model:filters="filters"
      export-filename="Vehicle Offers"
      :value="filteredOffers"
      :loading="isLoading"
      removable-sort
      sort-field="updatedAt"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-vehicle-offers"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="[
        'id',
        'title',
        'vehicleCondition',
        'dealerships.displayName',
        'targetVehicles.make',
        'targetVehicles.modelMarketingName',
        'targetVehicles.model'
      ]"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between items-center gap-4">
          <div class="flex gap-4 items-center">
            <Input
              v-model="globalFilterValue"
              :placeholder="$t('ui.common.search', 1)"
              icon="search"
            />
            <div class="flex gap-2 items-center">
              <ToggleSwitch
                v-model="showArchivedOffers"
                size="sm"
              />
              <div class="font-medium">Show Archived Offers</div>
            </div>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </div>
          <Select
            v-model="columnsSelected"
            :options="columns"
            label-key="header"
            value-key="field"
            :chips="false"
            mode="multiple"
            on-select-return-as-object
            class="max-w-[220px]"
            placeholder="Select Columns"
            @change="onToggle"
          />
        </div>
      </template>

      <Column
        v-if="isColumnSelected('id')"
        field="id"
        header="ID"
        :sortable="true"
      />

      <Column
        v-if="isColumnSelected('title')"
        field="title"
        header="Title"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="max-w-[22rem]">
            <div class="font-medium">{{ data.title }}</div>
          </div>
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('vehicleCondition')"
        field="vehicleCondition"
        header="Condition"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            :rounded="false"
            outlined
            size="sm"
          >
            {{ formatVehicleOfferCondition(data.vehicleCondition) }}
          </Chip>
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('depositContribution')"
        field="depositContribution"
        header="Deposit Contribution"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ formatDepositContribution(data.depositContribution) }}
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('startDate')"
        field="startDate"
        header="Start"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ formatOfferDate(data.startDate) }}
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('endDate')"
        field="endDate"
        header="End"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ formatOfferDate(data.endDate) }}
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('dealershipIds')"
        field="dealershipIds"
        header="Dealerships"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <ChipList
            v-if="data.dealerships.length"
            :items="formatOfferDealershipChipList(data.dealerships)"
            :max-shown="3"
            :overflow-chip-props="{ size: 'sm' }"
          />
          <div v-else>—</div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="dealershipFilterOptions"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            class="min-w-64"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('targetVehicles')"
        header="Target Vehicles"
      >
        <template #body="{ data }">
          <div
            class="max-w-[18rem] truncate"
            :title="formatOfferTargetVehicles(data.targetVehicles)"
          >
            {{ formatOfferTargetVehicles(data.targetVehicles) }}
          </div>
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('isArchived')"
        field="isArchived"
        header="Status"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isArchived"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="secondary"
          >
            Archived
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Active
          </Chip>
        </template>
      </Column>

      <Column
        v-if="isColumnSelected('updatedAt')"
        field="updatedAt"
        header="Updated"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ formatOfferTimestamp(data.updatedAt || data.createdAt) }}
        </template>
      </Column>

      <Column>
        <template #body="{ data }">
          <Dropdown>
            <template #triggerContent>
              <Button
                icon="ellipsis-vertical"
                plain
                severity="secondary"
                alt="Offer actions"
              />
            </template>
            <DropdownItem @click="openEditModal(data.id)">
              <div class="flex items-center">
                <Icon
                  type="pencil"
                  fa-style="fas"
                  class="mr-2"
                />
                Edit
              </div>
            </DropdownItem>
            <DropdownItem
              v-if="!data.isArchived"
              @click="archiveOffer(data)"
            >
              <div class="flex items-center">
                <Icon
                  type="box-archive"
                  fa-style="fas"
                  class="mr-2"
                />
                Archive
              </div>
            </DropdownItem>
            <DropdownItem @click="deleteOffer(data)">
              <div class="flex items-center text-error">
                <Icon
                  type="trash"
                  fa-style="fas"
                  class="mr-2"
                />
                Delete
              </div>
            </DropdownItem>
          </Dropdown>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="offerModalIsOpen"
      :title="isEditingOffer ? 'Edit Offer' : 'New Offer'"
      icon="car"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="offerModalHasUnsavedChanges"
      close-text="Cancel"
      :ok-text="isEditingOffer ? 'Save' : 'Create'"
      :show-loader-on-confirm="true"
      :is-confirming="isSubmittingOffer"
      :disable-close="isSubmittingOffer"
      min-width="720"
      overflow-behaviour="visible"
      @close-modal="closeOfferModal()"
      @ok-modal="submitOffer()"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormGroup
          class="md:col-span-2"
          label="Title"
          :is-required="v$.title.required?.$invalid"
        >
          <Input
            v-model="offerTitleModel"
            :has-error="v$.title.$error"
          />
          <InputError :has-error="v$.title.$error">{{ v$.title.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Vehicle Condition"
          :is-required="v$.vehicleCondition.required?.$invalid"
        >
          <Select
            v-model="offerVehicleConditionModel"
            :options="vehicleConditionOptions"
            value-key="value"
            label-key="label"
            :has-error="v$.vehicleCondition.$error"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$.vehicleCondition.$error">
            {{ v$.vehicleCondition.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="Deposit Contribution"
          :is-required="v$.depositContribution.required?.$invalid"
        >
          <Input
            v-model="offerDepositContributionModel"
            type="number"
            :min="0"
            :has-error="v$.depositContribution.$error"
          />
          <InputError :has-error="v$.depositContribution.$error">
            {{ v$.depositContribution.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="Start Date"
          :is-required="v$.startDate.required?.$invalid"
        >
          <DatePicker
            v-model="offerStartDateModel"
            :has-error="v$.startDate.$error"
            date-format="YYYY-MM-DD"
            display-format="DD/MM/YYYY"
          />
          <InputError :has-error="v$.startDate.$error">{{ v$.startDate.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="End Date"
          :is-required="v$.endDate.required?.$invalid"
        >
          <DatePicker
            v-model="offerEndDateModel"
            :has-error="v$.endDate.$error"
            :min-date="offerStartDateModel"
            date-format="YYYY-MM-DD"
            display-format="DD/MM/YYYY"
          />
          <InputError :has-error="v$.endDate.$error">{{ v$.endDate.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          class="md:col-span-2"
          label="Dealerships"
          :is-required="v$.dealershipIds.required?.$invalid"
        >
          <Select
            v-model="offerFormState.dealershipIds"
            :options="dealerships"
            value-key="id"
            label-key="displayName"
            searchable
            mode="multiple"
            chips
            :has-error="v$.dealershipIds.$error"
            placeholder="Select dealerships"
            clearable
          >
            <template #option="slotProps">
              <div>{{ slotProps.option.displayName || slotProps.option.name }}</div>
            </template>
          </Select>
          <InputError :has-error="v$.dealershipIds.$error">
            {{ v$.dealershipIds.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          class="md:col-span-2"
          label="Target Vehicles"
          :is-required="v$.targetVehicleIds.required?.$invalid"
        >
          <Select
            v-model="offerFormState.targetVehicleIds"
            :options="targetVehicleOptions"
            value-key="id"
            label-key="label"
            searchable
            mode="multiple"
            chips
            :has-error="v$.targetVehicleIds.$error"
            placeholder="Select target vehicles"
            clearable
          >
            <template #option="slotProps">
              <div class="flex items-center gap-3">
                <img
                  v-if="slotProps.option.imageUrl"
                  :src="slotProps.option.imageUrl"
                  :alt="slotProps.option.label"
                  class="h-8 w-12 rounded object-cover bg-secondary"
                />
                <div>{{ slotProps.option.label }}</div>
              </div>
            </template>
          </Select>
          <InputError :has-error="v$.targetVehicleIds.$error">
            {{ v$.targetVehicleIds.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </Modal>
  </teleport>
</template>

<script setup lang="ts">
// libraries
import { computed, onMounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'

// components
import Button from '@/components/button/Button.vue'
import Card from '@/components/card/Card.vue'
import ChipList from '@/components/chip/ChipList.vue'
import Chip from '@/components/chip/Chip.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Select from '@/components/forms/Select.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Icon from '@/components/icon/Icon.vue'
import Modal from '@/components/modal/Modal.vue'
import PageHeader from '@/components/page/PageHeader.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// types
import type { Dealership } from '@/types/domain/dealership'
import type {
  VehicleOffer,
  VehicleOfferCondition,
  VehicleOfferFormState,
  VehicleOfferTargetVehicle,
  VehicleOfferTargetVehicleOption
} from '@/types/domain/vehicleOffer'

// utilities
import { alertToast } from '@/utilities/notification'
import { formatCurrency } from '@/utilities/number/formatNumber'

// tables
import { FilterMatchMode } from '@primevue/core/api'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'

interface AvailableVehicleApiItem {
  id: number
  make?: string | null
  model?: string | null
  image?: string | null
}

interface ManufacturerApiItem {
  id?: number
  name: string
}

interface VehicleOfferTableColumn {
  field: string
  header: string
  hideByDefault: boolean
}

/**
 * Returns a clean vehicle offer form state for create and reset flows.
 */
function createEmptyVehicleOfferFormState(): VehicleOfferFormState {
  return {
    title: null,
    vehicleCondition: 'all',
    depositContribution: null,
    startDate: null,
    endDate: null,
    dealershipIds: [],
    targetVehicleIds: []
  }
}

/**
 * Returns a shallow-safe clone of the editable vehicle offer state.
 */
function cloneVehicleOfferFormState(state: VehicleOfferFormState): VehicleOfferFormState {
  return {
    ...state,
    dealershipIds: [...state.dealershipIds],
    targetVehicleIds: [...state.targetVehicleIds]
  }
}

/**
 * Returns true when the provided value contains non-whitespace text.
 */
function hasText(value: unknown): boolean {
  return typeof value === 'string' ? value.trim().length > 0 : false
}

/**
 * Converts empty or whitespace-only strings to null before sending them to the API.
 */
function trimToNull(value: string | null): string | null {
  return value && value.trim().length ? value.trim() : null
}

/**
 * Normalizes an API vehicle item into a target vehicle option for the modal.
 */
function normalizeTargetVehicleOption(vehicle: AvailableVehicleApiItem): VehicleOfferTargetVehicleOption {
  const manufacturerMatch = manufacturersList.value.find(manufacturer => manufacturer.name === vehicle.make)
  const make = manufacturerMatch?.name || vehicle.make || null
  const modelMarketingName = null
  const model = vehicle.model || null
  const label = [make, model].filter(Boolean).join(' ')

  return {
    id: vehicle.id,
    label: label || `Vehicle #${vehicle.id}`,
    make,
    modelMarketingName,
    model,
    imageUrl: vehicle.image || null
  }
}

/**
 * Normalizes API offer data into the editable form state shape used by the modal.
 */
function normalizeVehicleOfferFormState(offer: VehicleOffer): VehicleOfferFormState {
  return {
    title: offer.title,
    vehicleCondition: offer.vehicleCondition,
    depositContribution: offer.depositContribution,
    startDate: offer.startDate,
    endDate: offer.endDate,
    dealershipIds: offer.dealerships.map(dealership => dealership.id),
    targetVehicleIds: offer.targetVehicles.map(vehicle => vehicle.id)
  }
}

/**
 * Formats the current form state into the payload expected by the vehicle offers API.
 */
function serializeVehicleOfferFormState() {
  return {
    title: trimToNull(offerFormState.value.title),
    vehicleCondition: offerFormState.value.vehicleCondition,
    depositContribution: Number(offerFormState.value.depositContribution),
    startDate: offerFormState.value.startDate,
    endDate: offerFormState.value.endDate,
    dealershipIds: [...offerFormState.value.dealershipIds],
    targetVehicleIds: [...offerFormState.value.targetVehicleIds]
  }
}

const offers = ref<VehicleOffer[]>([])
const dealerships = ref<Dealership[]>([])
const availableVehicles = ref<AvailableVehicleApiItem[]>([])
const manufacturersList = ref<ManufacturerApiItem[]>([])

const offerColumnsStorageKey = 'settings-vehicle-offers-columns'
const columns = ref<VehicleOfferTableColumn[]>([
  { field: 'id', header: 'ID', hideByDefault: true },
  { field: 'title', header: 'Title', hideByDefault: false },
  { field: 'vehicleCondition', header: 'Condition', hideByDefault: false },
  { field: 'depositContribution', header: 'Deposit Contribution', hideByDefault: false },
  { field: 'startDate', header: 'Start', hideByDefault: false },
  { field: 'endDate', header: 'End', hideByDefault: false },
  { field: 'dealershipIds', header: 'Dealerships', hideByDefault: true },
  { field: 'targetVehicles', header: 'Target Vehicles', hideByDefault: true },
  { field: 'isArchived', header: 'Status', hideByDefault: false },
  { field: 'updatedAt', header: 'Updated', hideByDefault: false }
])
const columnsSelected = ref<VehicleOfferTableColumn[]>([])

const columnsSelectedStorage = localStorage.getItem(offerColumnsStorageKey)

if (columnsSelectedStorage) {
  try {
    const parsedColumns = JSON.parse(columnsSelectedStorage) as Array<{ field?: string }>
    columnsSelected.value = columns.value.filter(column =>
      parsedColumns.some(parsedColumn => parsedColumn.field === column.field)
    )
  } catch {
    columnsSelected.value = columns.value.filter(column => !column.hideByDefault)
  }
} else {
  columnsSelected.value = columns.value.filter(column => !column.hideByDefault)
}

const offerModalIsOpen = ref(false)
const offerModalHasUnsavedChanges = ref(false)
const editingOfferId = ref<number | null>(null)
const showArchivedOffers = ref(false)
const offerFormState = ref<VehicleOfferFormState>(createEmptyVehicleOfferFormState())
const offerFormInitialState = ref<VehicleOfferFormState>(createEmptyVehicleOfferFormState())

const offersRequest = useApiRequest()
const offerDetailsRequest = useApiRequest()
const createOfferRequest = useApiRequest()
const updateOfferRequest = useApiRequest()
const archiveOfferRequest = useApiRequest()
const deleteOfferRequest = useApiRequest()
const dealershipsRequest = useApiRequest()
const manufacturersRequest = useApiRequest()
const availableVehiclesRequest = useApiRequest()

const filters = ref<{
  global: { value: string | undefined; matchMode: string }
  dealershipIds: { value: number[] | null; matchMode: string }
}>({
  global: { value: undefined, matchMode: FilterMatchMode.CONTAINS },
  dealershipIds: { value: null, matchMode: FilterMatchMode.IN }
})

/**
 * Available vehicle condition options shared by the table and modal.
 */
const vehicleConditionOptions = [
  { value: 'all', label: 'All Vehicles' },
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' }
]

/**
 * Returns true while the table is loading.
 */
const isLoading = computed<boolean>(() => {
  return offersRequest.isLoading.value
})

/**
 * Returns true while the modal submit request is in progress.
 */
const isSubmittingOffer = computed<boolean>(() => {
  return createOfferRequest.isLoading.value || updateOfferRequest.isLoading.value
})

/**
 * Returns true when the modal is editing an existing offer.
 */
const isEditingOffer = computed<boolean>(() => {
  return editingOfferId.value !== null
})

/**
 * Normalized target vehicle options for the modal multi-select.
 */
const targetVehicleOptions = computed<VehicleOfferTargetVehicleOption[]>(() => {
  return availableVehicles.value.map(normalizeTargetVehicleOption)
})

/**
 * Dealership options for the datatable column filter.
 */
const dealershipFilterOptions = computed<Array<{ value: number; label: string }>>(() => {
  return dealerships.value.map(dealership => ({
    value: dealership.id,
    label: dealership.displayName || dealership.name
  }))
})

/**
 * Applies the custom dealerships filter before PrimeVue handles the remaining table filters.
 */
const filteredOffers = computed<VehicleOffer[]>(() => {
  const selectedDealershipIds = filters.value.dealershipIds.value

  let filteredVehicleOffers = offers.value

  if (!showArchivedOffers.value) {
    filteredVehicleOffers = filteredVehicleOffers.filter(offer => !offer.isArchived)
  }

  if (!Array.isArray(selectedDealershipIds) || selectedDealershipIds.length === 0) {
    return filteredVehicleOffers
  }

  return filteredVehicleOffers.filter(offer => {
    return offer.dealerships.some(dealership => selectedDealershipIds.includes(dealership.id))
  })
})

/**
 * Returns true when the given table column is currently selected.
 */
function isColumnSelected(field: string): boolean {
  return columnsSelected.value.some(column => column.field === field)
}

/**
 * Persists the current table column visibility state.
 */
function onToggle(): void {
  columnsSelected.value = columns.value.filter(column => {
    return columnsSelected.value.some(selectedColumn => selectedColumn.field === column.field)
  })
  localStorage.setItem(offerColumnsStorageKey, JSON.stringify(columnsSelected.value))
}

/**
 * String-backed global search filter model for the datatable header input.
 */
const globalFilterValue = computed<string | undefined>({
  get() {
    return filters.value.global.value
  },
  set(value) {
    filters.value.global.value = value
  }
})

/**
 * String-backed title model for the modal input component.
 */
const offerTitleModel = computed<string | undefined>({
  get() {
    return offerFormState.value.title ?? undefined
  },
  set(value) {
    offerFormState.value.title = value ?? null
  }
})

/**
 * Select-friendly vehicle condition model for the modal.
 */
const offerVehicleConditionModel = computed<VehicleOfferCondition | undefined>({
  get() {
    return offerFormState.value.vehicleCondition ?? undefined
  },
  set(value) {
    offerFormState.value.vehicleCondition = value ?? null
  }
})

/**
 * String-backed deposit contribution model for the numeric input component.
 */
const offerDepositContributionModel = computed<string | undefined>({
  get() {
    if (offerFormState.value.depositContribution === null || offerFormState.value.depositContribution === '') {
      return undefined
    }

    return String(offerFormState.value.depositContribution)
  },
  set(value) {
    offerFormState.value.depositContribution = value === undefined || value === '' ? null : value
  }
})

/**
 * Date-picker friendly start date model for the modal.
 */
const offerStartDateModel = computed<string | undefined>({
  get() {
    return offerFormState.value.startDate ?? undefined
  },
  set(value) {
    offerFormState.value.startDate = value ?? null
  }
})

/**
 * Date-picker friendly end date model for the modal.
 */
const offerEndDateModel = computed<string | undefined>({
  get() {
    return offerFormState.value.endDate ?? undefined
  },
  set(value) {
    offerFormState.value.endDate = value ?? null
  }
})

/**
 * Validation rules for the vehicle offer modal.
 */
const validationRules = computed(() => ({
  title: {
    required: helpers.withMessage('Title is required', value => hasText(value))
  },
  vehicleCondition: {
    required: helpers.withMessage('Vehicle condition is required', required)
  },
  depositContribution: {
    required: helpers.withMessage('Deposit contribution is required', value => {
      return value !== null && value !== '' && !Number.isNaN(Number(value))
    }),
    positive: helpers.withMessage('Deposit contribution must be 0 or greater', value => {
      if (value === null || value === '') {
        return false
      }

      return Number(value) >= 0
    })
  },
  startDate: {
    required: helpers.withMessage('Start date is required', required)
  },
  endDate: {
    required: helpers.withMessage('End date is required', required),
    isOnOrAfterStartDate: helpers.withMessage('End date must be on or after the start date', value => {
      if (!offerFormState.value.startDate || typeof value !== 'string') {
        return true
      }

      return !dayjs(value).isBefore(dayjs(offerFormState.value.startDate), 'day')
    })
  },
  dealershipIds: {
    required: helpers.withMessage('Select at least one dealership', value => Array.isArray(value) && value.length > 0)
  },
  targetVehicleIds: {
    required: helpers.withMessage(
      'Select at least one target vehicle',
      value => Array.isArray(value) && value.length > 0
    )
  }
}))

const v$ = useVuelidate(validationRules, offerFormState, { $stopPropagation: true })

/**
 * Loads the full vehicle offers list for the settings table.
 */
async function fetchOffers(): Promise<void> {
  try {
    const response = await offersRequest.send<VehicleOffer[]>({
      endpoint: '/v1/vehicle-offers',
      method: 'GET',
      params: {
        includeArchived: true
      }
    })

    if (response.success && Array.isArray(response.data)) {
      offers.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch vehicle offers', 'error')
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch vehicle offers'
    alertToast('Error', message, 'error')
  }
}

/**
 * Loads dealerships so offers can be scoped in the modal and labelled in the table.
 */
async function fetchDealerships(): Promise<void> {
  try {
    const response = await dealershipsRequest.send<Dealership[]>({
      endpoint: '/v1/dealerships',
      method: 'GET',
      params: {
        showAll: true,
        showDisabled: true
      }
    })

    if (response.success && Array.isArray(response.data)) {
      dealerships.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch dealerships', 'error')
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch dealerships'
    alertToast('Error', message, 'error')
  }
}

/**
 * Loads manufacturers so the target vehicle options can use the canonical make labels.
 */
async function fetchManufacturers(): Promise<void> {
  try {
    const response = await manufacturersRequest.send<ManufacturerApiItem[]>({
      endpoint: '/v1/manufacturers',
      method: 'GET'
    })

    if (response.success && Array.isArray(response.data)) {
      manufacturersList.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch manufacturers', 'error')
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch manufacturers'
    alertToast('Error', message, 'error')
  }
}

/**
 * Loads the available vehicle models used by the offer form.
 */
async function fetchAvailableVehicles(): Promise<void> {
  try {
    const response = await availableVehiclesRequest.send<AvailableVehicleApiItem[]>({
      endpoint: '/v1/vehicles/models',
      method: 'GET'
    })

    if (response.success && Array.isArray(response.data)) {
      availableVehicles.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch vehicle models', 'error')
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch vehicle models'
    alertToast('Error', message, 'error')
  }
}

/**
 * Clears persisted table filters and restores the defaults.
 */
function clearFilters(): void {
  sessionStorage.removeItem('dt-settings-vehicle-offers')
  filters.value = {
    global: { value: undefined, matchMode: FilterMatchMode.CONTAINS },
    dealershipIds: { value: null, matchMode: FilterMatchMode.IN }
  }
}

/**
 * Resets modal state back to its initial create-ready values.
 */
function closeOfferModal(): void {
  offerModalIsOpen.value = false
  offerModalHasUnsavedChanges.value = false
  editingOfferId.value = null
  offerFormState.value = createEmptyVehicleOfferFormState()
  offerFormInitialState.value = createEmptyVehicleOfferFormState()
  v$.value.$reset()
}

/**
 * Opens the modal in create mode using the default form state.
 */
function openCreateModal(): void {
  editingOfferId.value = null
  offerFormState.value = createEmptyVehicleOfferFormState()
  offerFormInitialState.value = createEmptyVehicleOfferFormState()
  offerModalHasUnsavedChanges.value = false
  offerModalIsOpen.value = true
  v$.value.$reset()
}

/**
 * Loads a single offer and opens the modal in edit mode.
 */
async function openEditModal(offerId: number): Promise<void> {
  try {
    const response = await offerDetailsRequest.send<VehicleOffer>({
      endpoint: `/v1/vehicle-offers/${offerId}`,
      method: 'GET'
    })

    if (response.success && response.data) {
      const normalizedOfferState = normalizeVehicleOfferFormState(response.data)
      editingOfferId.value = offerId
      offerFormInitialState.value = cloneVehicleOfferFormState(normalizedOfferState)
      offerFormState.value = cloneVehicleOfferFormState(normalizedOfferState)
      offerModalHasUnsavedChanges.value = false
      offerModalIsOpen.value = true
      v$.value.$reset()
      return
    }

    alertToast('Error', response.message || 'Failed to load vehicle offer', 'error')
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load vehicle offer'
    alertToast('Error', message, 'error')
  }
}

/**
 * Persists a new or existing vehicle offer using the appropriate CRUD endpoint.
 */
async function submitOffer(): Promise<void> {
  const isFormValid = await v$.value.$validate()

  if (!isFormValid) {
    return
  }

  const payload = JSON.stringify(serializeVehicleOfferFormState())

  try {
    if (isEditingOffer.value && editingOfferId.value !== null) {
      const response = await updateOfferRequest.send<VehicleOffer>({
        endpoint: `/v1/vehicle-offers/${editingOfferId.value}`,
        method: 'PATCH',
        data: payload
      })

      if (!response.success) {
        alertToast('Error', response.message || 'Failed to update vehicle offer', 'error')
        return
      }

      alertToast('Success!', 'Vehicle offer updated', 'success')
    } else {
      const response = await createOfferRequest.send<VehicleOffer>({
        endpoint: '/v1/vehicle-offers',
        method: 'POST',
        data: payload
      })

      if (!response.success) {
        alertToast('Error', response.message || 'Failed to create vehicle offer', 'error')
        return
      }

      alertToast('Success!', 'Vehicle offer created', 'success')
    }

    closeOfferModal()
    await fetchOffers()
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to save vehicle offer'
    alertToast('Error', message, 'error')
  }
}

/**
 * Archives an offer after the user confirms the destructive action.
 */
async function archiveOffer(offer: VehicleOffer): Promise<void> {
  const confirmation = await Swal.fire({
    title: 'Archive offer?',
    text: `This will archive "${offer.title}" and remove it from active offers.`,
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Archive offer',
    confirmButtonColor: 'var(--bg-warning-solid)',
    cancelButtonText: 'Cancel'
  })

  if (!confirmation.isConfirmed) {
    return
  }

  try {
    const response = await archiveOfferRequest.send({
      endpoint: `/v1/vehicle-offers/${offer.id}/archive`,
      method: 'PATCH'
    })

    if (!response.success) {
      alertToast('Error', response.message || 'Failed to archive vehicle offer', 'error')
      return
    }

    alertToast('Success!', 'Vehicle offer archived', 'success')
    await fetchOffers()
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to archive vehicle offer'
    alertToast('Error', message, 'error')
  }
}

/**
 * Deletes an offer after the user confirms the destructive action.
 */
async function deleteOffer(offer: VehicleOffer): Promise<void> {
  const confirmation = await Swal.fire({
    title: 'Delete offer?',
    text: `This will permanently remove "${offer.title}".`,
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Delete offer',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  })

  if (!confirmation.isConfirmed) {
    return
  }

  try {
    const response = await deleteOfferRequest.send({
      endpoint: `/v1/vehicle-offers/${offer.id}`,
      method: 'DELETE'
    })

    if (!response.success) {
      alertToast('Error', response.message || 'Failed to delete vehicle offer', 'error')
      return
    }

    alertToast('Success!', 'Vehicle offer deleted', 'success')
    await fetchOffers()
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete vehicle offer'
    alertToast('Error', message, 'error')
  }
}

/**
 * Formats a vehicle offer condition for table display.
 */
function formatVehicleOfferCondition(condition: VehicleOfferCondition | null): string {
  if (condition === 'new') {
    return 'New'
  }

  if (condition === 'used') {
    return 'Used'
  }

  return 'All Vehicles'
}

/**
 * Formats the deposit contribution for table display.
 */
function formatDepositContribution(value: number): string {
  return formatCurrency(value, 'GBP', 0) || 'GBP 0'
}

/**
 * Formats offer dates for the datatable.
 */
function formatOfferDate(value: string | null): string {
  return value ? dayjs(value).format('DD MMM YYYY') : '—'
}

/**
 * Formats dealership data for ChipList display in the datatable.
 */
function formatOfferDealershipChipList(
  offerDealerships: VehicleOffer['dealerships']
): Array<{ label: string; size: string }> {
  return offerDealerships.map(dealership => ({
    label: dealership.displayName || `Dealership #${dealership.id}`,
    size: 'sm'
  }))
}

/**
 * Formats target vehicle labels for the table display.
 */
function formatOfferTargetVehicles(targetVehicles: VehicleOfferTargetVehicle[]): string {
  if (!targetVehicles.length) {
    return '—'
  }

  return targetVehicles
    .map(vehicle => [vehicle.make, vehicle.modelMarketingName || vehicle.model].filter(Boolean).join(' '))
    .join(', ')
}

/**
 * Formats nullable timestamps using the shared dayjs display convention.
 */
function formatOfferTimestamp(value: string | null): string {
  return value ? dayjs(value).format('ll') : '—'
}

watch(
  offerFormState,
  newValue => {
    offerModalHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(offerFormInitialState.value)
  },
  { deep: true }
)

onMounted(() => {
  void Promise.all([fetchOffers(), fetchDealerships(), fetchManufacturers(), fetchAvailableVehicles()])
})
</script>

<style scoped></style>
